import { Component, Input, OnInit } from '@angular/core';
import { EntityType, IUser } from '@portal/entity-services/interfaces';
import { forkJoin, from, Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { IOrganisation } from '@portal/entity-services/interfaces';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { catchError, distinct, finalize, map, switchMap, toArray } from 'rxjs/operators';
import { UserService } from '@portal/entity-services/users/src/lib/services/user.service';
import { IGroupViewDetailEdit } from '../../interfaces/group-view-detail-edit.interface';
import { DataServiceError } from '@ngrx/data';
import { Router } from '@angular/router';
import { NotifierService } from '@portal/shared/ui/notifier/src';
import { ToastService } from '@portal/shared/ui/toast';
import get from 'lodash/get';
import { MenuRegion } from '@portal/shared/ui/layouts/src/lib/top-navbar/enum/menu-region.enum';
import { HeroService } from '@portal/shared/ui/layouts/src/lib/hero/services/hero.service';
import { SearchSelector } from '@portal/shared/ui/filter';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { Features } from 'environments/enums/features.enum';
import { AcquirerTypes } from '@portal/entity-services/payment-contracts/src/lib/lists/acquirer-type.list';
import { ProcessorTypes } from '@portal/entity-services/payment-contracts/src/lib/components/form/processor-type.list';

declare const $localize;

@Component({
  selector: 'portal-details-organization-group',
  templateUrl: './details-organization-group.component.html',
  styleUrls: ['./details-organization-group.component.scss'],
  standalone: false,
})
export class DetailsOrganizationGroupComponent implements OnInit {
  loading$: Observable<boolean>;
  @Input() entityUId: string;
  organizationGroup: IOrganisation;
  parentOrganization: IOrganisation;
  subOrganisations: IOrganisation[];
  usersForOrganizationGroup: IUser[];

  @Input() groupViewDetailEdit: IGroupViewDetailEdit = {
    groupOrganization: {},
    parentOrganization: {},
    users: [],
    subOrganizations: [],
  };

  maximumLimitOrgToAdd = 100;
  minimumLimitOrgToAdd = 2;
  preSelectedOrganizationNumber: number;
  isOrganisationLoading: boolean;
  isUsersLoading: boolean;
  isEditForm = false;
  isButtonDisabled = false;
  isSaving = false;
  selectedEntityListGroupOrganisation: string[] = [];
  entityListGroupOrganisation = [];
  isGroupHierarchyRestrictByActive = false;
  isFromOrganisations = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private organisationService: OrganisationService,
    private userService: UserService,
    private notifierService: NotifierService,
    private toastService: ToastService,
    private router: Router,
    private heroService: HeroService,
    private featureToggleService: FeatureToggle,
    private acquirerTypes: AcquirerTypes,
    private processorTypes: ProcessorTypes,
  ) {
    this.isFromOrganisations =
      this.activatedRoute.snapshot.queryParamMap.get('from') === 'organisations';
    const eventId = this.activatedRoute.snapshot.paramMap.get('id');
    if (eventId) {
      this.entityUId = eventId;
      this.organisationService.getByKey(this.entityUId)?.subscribe((entity) => {
        this.getEntitySetup(entity);
        this.getGroupEntityRelationships();
        this.findUsersByEntityId();
      });
    }
    this.loading$ = organisationService.loading$;
  }

  ngOnInit(): void {
    this.heroService.showTitle($localize`Administration`, MenuRegion.Administration);

    // Check if GROUP_HIERARCHY_RESTRICT_BY feature is active
    this.isGroupHierarchyRestrictByActive = this.featureToggleService.isConfigFeatureActive(
      Features.GroupHierarchyRestrictBy
    );
  }

  getGroupEntityRelationships(): void {
    this.isOrganisationLoading = true;
    this.organisationService
      .getGroupEntityRelationships(this.organizationGroup?.entityUid)
      ?.pipe(
        switchMap((result) => {
          return forkJoin(
            result?.map((val) =>
              this.organisationService.getTotalCount({
                parentId: val[SearchSelector.EntityUid],
              }),
            ),
          ).pipe(
            map((val) =>
              val.map((totalCountData, i) => ({
                ...result[i],
                id: result[i].entityUid,
                count: totalCountData.count,
                hasChildren: totalCountData.count > 0,
              })),
            ),
          );
        }),
      )
      ?.pipe(finalize(() => (this.isOrganisationLoading = false)))
      ?.subscribe(
        (entitiesUid: IOrganisation[]) => {
          this.groupViewDetailEdit.subOrganizations = entitiesUid || [];
          this.subOrganisations = entitiesUid;
          this.selectedEntityListGroupOrganisation = entitiesUid?.map((entity) => entity.entityUid);
        },
        (errorUserUpdate: DataServiceError) => {
          this.notifierService.error(
            $localize`Error` +
              ` (${get(errorUserUpdate, 'error.status')}) ${get(
                errorUserUpdate,
                'error.error.message',
              )}`,
          );
        },
      );
  }

  findUsersByEntityId(): void {
    this.isUsersLoading = true;
    this.userService
      .getUsersByEntity(this.organizationGroup?.entityUid)
      .pipe(
        finalize(() => {
          this.isUsersLoading = false;
        }),
        switchMap(from),
        distinct((user: IUser) => user.userUid),
        toArray(),
      )
      .subscribe(
        (users) => {
          this.groupViewDetailEdit.users = users || [];
          this.usersForOrganizationGroup = users;
        },
        (errorUserUpdate: DataServiceError) => {
          this.notifierService.error(
            $localize`Error` +
              ` (${get(errorUserUpdate, 'error.status')}) ${get(
                errorUserUpdate,
                'error.error.message',
              )}`,
          );
        },
      );
  }

  onFormValidated(data: Record<string, any>): void {
    if (
      this.preSelectedOrganizationNumber <= this.maximumLimitOrgToAdd &&
      this.preSelectedOrganizationNumber >= this.minimumLimitOrgToAdd
    ) {
      this.replaceRelationshipsUpdateUsers(data);
    }
  }

  closeEditForm(): void {
    this.isEditForm = false;
  }

  onSelectedOrganizationNumber(organizationsNumber: number): void {
    this.preSelectedOrganizationNumber = organizationsNumber;
  }

  getUsersByIds(userIds: string[]): Observable<IUser>[] {
    return userIds.map((userUid) => {
      if (!userUid) {
        return of(null);
      }
      return this.userService.getByKey(userUid).pipe(catchError(() => of({ userUid })));
    });
  }

  onEditGroupForm(): void {
    this.isEditForm = true;
  }

  replaceRelationships(
    paramEntityUids: Record<string, any>,
    entityGroupUid: string,
  ): Observable<any> {
    return this.organisationService.replaceGroupEntityRelationships(
      paramEntityUids,
      entityGroupUid,
    );
  }

  /**
   * data.entityUids = Entities From Form to edit
   * subOrgRelatedFromParentEntity = Entities relationships  Related to Parent entity
   * @param data
   * return (there's relationship between Group organization and sub entities)
   */
  isRelationshipAndSubEntitiesUpdated(data: Record<string, any>): boolean {
    const subOrgRelatedFromParentEntity = this.groupViewDetailEdit?.subOrganizations?.map(
      (org) => org.entityUid,
    );

    // Check if sub-organizations have changed
    return (!this.isTwoArraysEqual(data.entityUids, subOrgRelatedFromParentEntity) &&
      subOrgRelatedFromParentEntity?.length) as unknown as boolean;
  }

  createGroupEntityRelationships(
    paramEntityUids: Record<string, any>,
    entityGroupUid: string,
  ): Observable<any> {
    return this.organisationService.createGroupEntityRelationships(paramEntityUids, entityGroupUid);
  }

  replaceRelationshipsUpdateUsers(data: Record<string, any>): void {
    const singleEntityPayload: any = {
      name: data.name,
      parentEntityUid: data.parentEntityUid,
      entityType: EntityType.GROUP,
    };

    const entityUids: any = {
      entityUids: this.selectedEntityListGroupOrganisation,
    };

    let userIds = data?.userIds;
    userIds = userIds?.filter((id) => id);

    const groupEntityId = data.groupUid;
    const groupName = singleEntityPayload.name;
    this.isSaving = true;
    this.isButtonDisabled = true;

    // Prepare restrictByLabels for entity update if provided from the form and feature flag is active
    let restrictByLabels = null;
    if (this.isGroupHierarchyRestrictByActive && data.groupBy && data.selectedItems) {
      const labelPrefix = data.groupBy.toLowerCase() === 'acquirers' ? 'acquirer' : 'processor';
      restrictByLabels = [`${labelPrefix}:${data.selectedItems}`];
    } else if (this.isGroupHierarchyRestrictByActive) {
      // If feature flag is active but no selection, explicitly set empty array to clear existing values
      restrictByLabels = [];
    }

    this.updateEntity(groupName, groupEntityId, restrictByLabels);

    // There are relationships, and were updated or removed entities to update relationships
    if (this.isRelationshipAndSubEntitiesUpdated(data)) {
      this.replaceParentEntityRelationShips(entityUids, groupEntityId, userIds, groupName);
    } else {
      // There are no relationships between the group entity and other entities
      if (!this.groupViewDetailEdit?.subOrganizations.length) {
        this.createParentEntityRelationShips(entityUids, groupEntityId, userIds, groupName);
      } else if (userIds.length) {
        // Just update the users, in case of need
        this.updateTheUsersWithTheParentEntityId(userIds, groupEntityId, groupName);
      } else {
        this.isSaving = false;
        this.isButtonDisabled = false;
        this.isEditForm = false;
        this.toastService.showToast(
          $localize`Organization group ${groupName} has been edited successfully`,
        );
      }
    }
  }

  isTwoArraysEqual(a: any[], b: any[]): boolean {
    return a?.length === b?.length && a.filter((x) => !b.includes(x)).length === 0;
  }

  createParentEntityRelationShips(
    entityUids: Record<string, any[]>,
    groupEntityId: string,
    userIds: string[],
    groupName: string,
  ): void {
    this.createGroupEntityRelationships(entityUids, groupEntityId).subscribe(
      (relationship) => {
        // 204 - Entities associated with group entity.
        if (relationship.status === 204) {
          // No users selected
          if (!userIds?.length) {
            this.toastService.showToast(
              $localize`Organization group ${groupName} has been created`,
            );
            this.isEditForm = false;
            this.getGroupEntityRelationships();
          } else if (userIds.length) {
            // Here we need to update the users
            this.updateTheUsersWithTheParentEntityId(userIds, groupEntityId, groupName);
          }
        }
        this.isButtonDisabled = false;
        this.isSaving = false;
      },
      (errorRelationship: DataServiceError) => {
        this.notifierService.error(
          $localize`Error` +
            ` (${get(errorRelationship, 'error.status')}) ${get(
              errorRelationship,
              'error.error.message',
            )}`,
        );
        this.isButtonDisabled = false;
        this.isSaving = false;
      },
    );
  }

  updateTheUsersWithTheParentEntityId(
    userIds: string[],
    groupEntityId: string,
    groupName: string,
  ): void {
    let usersUpdated = 0;
    this.getUsersByIds(userIds).map((user) => {
      user.subscribe(
        (userData) => {
          const userToUpdate = { ...userData };
          // If the user is not associated with the organization group, then update the entity Id from user
          if (groupEntityId !== userToUpdate.entityUid) {
            userToUpdate.entityUid = groupEntityId;
            this.userService.update(userToUpdate).subscribe(
              () => {
                usersUpdated++;
                if (usersUpdated >= userIds.length) {
                  this.toastService.showToast(
                    $localize`Organization group ${groupName} has been edited successfully`,
                  );
                  this.isEditForm = false;
                  this.getGroupEntityRelationships();
                }
                this.isButtonDisabled = false;
                this.isSaving = false;
              },
              (errorUserUpdate: DataServiceError) => {
                this.notifierService.error(
                  $localize`Error` +
                    ` (${get(errorUserUpdate, 'error.status')}) ${get(
                      errorUserUpdate,
                      'error.error.message',
                    )}`,
                );
                this.isButtonDisabled = false;
                this.isSaving = false;
              },
            );
          } else {
            this.isButtonDisabled = false;
            this.isSaving = false;
          }
        },
        (errorGetUsersByIds: DataServiceError) => {
          this.notifierService.error(
            $localize`Error` +
              ` (${get(errorGetUsersByIds, 'error.status')}) ${get(
                errorGetUsersByIds,
                'error.error.message',
              )}`,
          );
          this.isButtonDisabled = false;
          this.isSaving = false;
        },
      );
    });
  }

  replaceParentEntityRelationShips(
    entityUids: Record<string, any[]>,
    groupEntityId: string,
    userIds: string[],
    groupName: string,
  ): void {
    this.replaceRelationships(entityUids, groupEntityId)?.subscribe(
      (relationship) => {
        // 204 - Entities associated with group entity.
        if (relationship.status === 204) {
          // No users selected
          if (!userIds?.length) {
            this.toastService.showToast(
              $localize`Organization group ${groupName} has been edited successfully`,
            );
            this.isEditForm = false;
            this.getGroupEntityRelationships();
          } else {
            // We update the users
            this.updateTheUsersWithTheParentEntityId(userIds, groupEntityId, groupName);
          }
        }
        this.isButtonDisabled = false;
        this.isSaving = false;
      },
      (errorCreateEntity: DataServiceError) => {
        this.notifierService.error(
          $localize`Error` +
            ` (${get(errorCreateEntity, 'error.status')}) ${
              get(errorCreateEntity, 'error.error.message') ||
              get(errorCreateEntity, 'error.message')
            }`,
        );
        this.isButtonDisabled = false;
        this.isSaving = false;
      },
    );
  }

  navigateToUsers(): void {
    localStorage.setItem('navigationFromOrgGroup', 'showUsers');
    this.router.navigate(['/administration', 'users']);
  }

  setEntityListGroupOrganisation(entityList: string[]): void {
    this.selectedEntityListGroupOrganisation = entityList;
  }

  private updateEntity(groupName: string, groupEntityId: string, restrictByLabels?: string[] | null): void {
    const hasNameChange = this.organizationGroup?.name !== groupName;
    const hasRestrictByLabelsChange = restrictByLabels !== null;

    if (hasNameChange || hasRestrictByLabelsChange) {
      const updatePayload: any = {};

      if (hasNameChange) {
        updatePayload.name = groupName;
      }

      if (hasRestrictByLabelsChange) {
        updatePayload.restrictByLabels = restrictByLabels;
      }

      this.organisationService
        .updateEntityWithPayload(groupEntityId, updatePayload)
        .subscribe((entity: IOrganisation) => {
          if (entity.name) {
            this.organizationGroup.name = entity.name;
          }
          if (restrictByLabels !== null) {
            (this.organizationGroup as any).restrictByLabels = restrictByLabels;
          }
          this.getEntitySetup(this.organizationGroup);
        });
    }
  }

  private getEntitySetup(entity: IOrganisation): void {
    this.organizationGroup = entity;
    this.parentOrganization = this.organizationGroup.parentEntity;
    this.groupViewDetailEdit.groupOrganization = entity || {};
    this.groupViewDetailEdit.parentOrganization = entity?.parentEntity || {};
  }

  getGroupByInfo(): boolean {
    const restrictByLabels = (this.organizationGroup as any)?.restrictByLabels;
    return this.isGroupHierarchyRestrictByActive &&
           restrictByLabels &&
           Array.isArray(restrictByLabels) &&
           restrictByLabels.length > 0;
  }

  getGroupByType(): string {
    const restrictByLabels = (this.organizationGroup as any)?.restrictByLabels;
    if (!restrictByLabels || !Array.isArray(restrictByLabels) || restrictByLabels.length === 0) {
      return '';
    }
    const firstLabel = restrictByLabels[0];
    const [type] = firstLabel.split(':');
    return type.toLowerCase() === 'acquirer' ? $localize`Acquirers` : $localize`Processors`;
  }

  getSelectedItems(): string[] {
    const restrictByLabels = (this.organizationGroup as any)?.restrictByLabels;
    if (!restrictByLabels || !Array.isArray(restrictByLabels)) {
      return [];
    }
    return restrictByLabels.map(label => label.split(':')[1]);
  }

  formatItemName(item: string): string {
    // Check if this is an acquirer or processor by looking at the context
    const restrictByLabels = (this.organizationGroup as any)?.restrictByLabels;
    if (restrictByLabels && restrictByLabels.length > 0) {
      const firstLabel = restrictByLabels[0];
      const [type] = firstLabel.split(':');

      if (type.toLowerCase() === 'acquirer') {
        // Use localized acquirer text
        return this.acquirerTypes.keyValue[item] || item.replace(/_/g, ' ');
      } else if (type.toLowerCase() === 'processor') {
        // Use localized processor text
        return this.processorTypes.keyValue[item] || item.replace(/_/g, ' ');
      }
    }
    // Fallback to replacing underscores
    return item.replace(/_/g, ' ');
  }
}
